import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PlaybookApiError,
  useApiClient,
  useModuleAvailability,
} from "@playbookai/api-clients";
import {
  kpiTtiBowlingApi,
  type KpiTtiBowlingEntry,
  type KpiTtiBowlingSeries,
  type RecordKpiTtiMonthlyActualInput,
  type SetKpiTtiMonthlyTargetsInput,
} from "../api/tti-bowling-client.js";

export const kpiTtiBowlingKey = (ttiId: string): string[] => [
  "kpi",
  "tti-bowling",
  ttiId,
];

/** What the hook answers while Hoshin is off, whatever is cached. */
const NO_SERIES = (): null => null;

export function useKpiTtiBowling(ttiId: string | null) {
  const config = useApiClient();
  // The series is Hoshin's. With Hoshin off it is not asked for, and a series
  // cached from before reads as none, so the target shows no "Target to
  // improve" link — the same quiet answer a 404 gives.
  const hoshinAvailable = useModuleAvailability().isAvailable("hoshin");
  return useQuery<KpiTtiBowlingSeries | null>({
    queryKey: kpiTtiBowlingKey(ttiId ?? "none"),
    queryFn: async () => {
      try {
        return await kpiTtiBowlingApi.getSeries(config, ttiId!);
      } catch (error) {
        // A standalone KPI target and a Hoshin target the actor cannot view are
        // intentionally indistinguishable. Neither should render a broken card.
        if (error instanceof PlaybookApiError && error.status === 404)
          return null;
        throw error;
      }
    },
    enabled: ttiId !== null && hoshinAvailable,
    ...(hoshinAvailable ? {} : { select: NO_SERIES }),
    staleTime: 30_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    retry: false,
  });
}

export function useSetKpiTtiMonthlyTargets(ttiId: string) {
  const config = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<KpiTtiBowlingSeries, Error, SetKpiTtiMonthlyTargetsInput>({
    mutationFn: (input) =>
      kpiTtiBowlingApi.setMonthlyTargets(config, ttiId, input),
    onSuccess: (series) => {
      queryClient.setQueryData(kpiTtiBowlingKey(ttiId), series);
      // KPI and Hoshin render the same monthly-plan rows under different query
      // keys. Keep the just-saved KPI view hot with the response, and mark every
      // mounted/dormant Hoshin bowling view stale so navigating to Ocean cannot
      // resurrect the pre-save monthly targets from cache.
      void queryClient.invalidateQueries({ queryKey: ["hoshin", "bowling"] });
    },
  });
}

export function useRecordKpiTtiMonthlyActual(ttiId: string) {
  const config = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<
    KpiTtiBowlingEntry,
    Error,
    { periodMonth: string; input: RecordKpiTtiMonthlyActualInput }
  >({
    mutationFn: ({ periodMonth, input }) =>
      kpiTtiBowlingApi.recordMonthlyActual(config, ttiId, periodMonth, input),
    onSuccess: (saved) => {
      queryClient.setQueryData<KpiTtiBowlingSeries>(
        kpiTtiBowlingKey(ttiId),
        (current) =>
          current
            ? {
                ...current,
                entries: [
                  ...current.entries.filter(
                    (entry) => entry.periodMonth !== saved.periodMonth,
                  ),
                  saved,
                ].sort((a, b) => a.periodMonth.localeCompare(b.periodMonth)),
              }
            : current,
      );
      void queryClient.invalidateQueries({ queryKey: ["hoshin", "bowling"] });
      void queryClient.invalidateQueries({
        queryKey: ["hoshin", "x-matrix"],
      });
    },
  });
}
